import type { ComponentType } from 'react';
import { Briefcase, Building2, Clock, Hash, MapPin } from 'lucide-react';
import { Skeleton } from '@clarix/ui';
import type { DirectoryMember } from '@clarix/shared-types';
import { DetailRow, SectionCard } from './ProfileSection';

interface WorkField {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  read: (member: DirectoryMember) => string | null | undefined;
  /** Hidden entirely when unset instead of showing a dash. */
  hideWhenEmpty?: boolean;
}

const WORK_FIELDS: WorkField[] = [
  { key: 'employee_id', label: 'Employee ID', icon: Hash, read: (m) => m.employee_id },
  { key: 'designation', label: 'Designation', icon: Briefcase, read: (m) => m.designation?.name },
  {
    key: 'department',
    label: 'Department',
    icon: Building2,
    read: (m) => m.department?.name,
    hideWhenEmpty: true,
  },
  {
    key: 'employment_type',
    label: 'Employment type',
    icon: Clock,
    read: (m) => m.employment_type?.name,
  },
  { key: 'work_location', label: 'Work location', icon: MapPin, read: (m) => m.work_location?.name },
];

interface WorkDetailsCardProps {
  /** The directory row; null/undefined renders every value as a dash. */
  member: DirectoryMember | null | undefined;
  isLoading?: boolean;
}

export function WorkDetailsCard({ member, isLoading = false }: WorkDetailsCardProps) {
  return (
    <SectionCard title="Work details">
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="divide-border divide-y">
          {WORK_FIELDS.map((field) => {
            const value = member ? field.read(member) : null;
            if (!value && field.hideWhenEmpty) return null;
            return (
              <DetailRow key={field.key} icon={field.icon} label={field.label} value={value || '—'} />
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
